import defaultContent from '../../../content/permissionFeatureList';
import { useTranslation } from 'react-i18next';
import Title from '../../common/Title/Title';
import Button from '../../common/Button/Button';
import Table, { TableActions } from '../../common/Table/Table';
import { FeatureRecord } from '../../../redux/services/types/types';
import { ColumnDef } from '../../common/Table/useTable';
import IconButton from '../../common/IconButton/IconButton';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import Confirm from '../../common/Confirm/Confirm';
import { useToast } from '../../common/Toast/ToastContext';
import { useDeleteFeatureMutation, useGetFeaturesQuery } from '../../../redux/services/featuresAPI';
import { GeneralStatusPill } from '../../common/TableUtils/TableUtils';

export const PermissionFeatureList = () => {
  const { t } = useTranslation(['permissionFeatureList']);
  const title = t('permissionFeatureList', defaultContent['permissionFeatureList']).toString();
  const navigate = useNavigate();

  const { data, isLoading } = useGetFeaturesQuery();

  const colDefs: Array<ColumnDef<FeatureRecord>> = [
    { field: 'name', header: 'Name' },
    { field: 'code', header: 'Code' },
    { field: 'applicationName', header: 'Application' },
    { field: 'platformName', header: 'Platform' },
    {
      field: 'status',
      header: 'Status',
      renderCell: (record) => <GeneralStatusPill status={record.status} />,
    },
    {
      header: 'Actions',
      renderCell: (record, colDef) => (
        <TableActions>
          <IconButton
            disabled={false}
            icon="edit"
            action={() => navigate(`/permission/feature/detail?id=${record.permissionFeatureId}`)}
            data-testid={`edit-button-${record.permissionFeatureId}`}
          />
          <DeleteFeatureButton id={record.permissionFeatureId} />
        </TableActions>
      ),
    },
  ];

  return (
    <div className="container">
      <Title text={title} />
      <div className="row">
        <div className="column-one right">
          <Button
            text="Add Feature"
            color="primary-black"
            disabled={false}
            action={function () {
              navigate('/permission/feature/detail');
            }}
          />
        </div>
      </div>
      <div className="h-2"></div>
      <Table
        colDefs={colDefs}
        data={data || []}
        isLoading={isLoading}
        getRowId={(feature) => feature.permissionFeatureId.toString()}
      />
    </div>
  );
};

export const DeleteFeatureButton = ({ id }: { id: number }) => {
  const [deleteUser, { isLoading }] = useDeleteFeatureMutation();
  const { refetch } = useGetFeaturesQuery();

  const [confirm, setConfirm] = useState(false);

  const { addToast } = useToast();

  const onDelete = () =>
    deleteUser(id)
      .unwrap()
      .then(() => {
        addToast({
          content: 'Feature deleted successfully !',
          type: 'success',
        });
        refetch();
        setConfirm(false);
      })
      .catch((err) => {
        console.error(err);
        addToast({
          content: 'Failed to delete feature !',
          type: 'error',
        });
      });

  return (
    <>
      <IconButton
        disabled={isLoading}
        icon="delete"
        action={() => setConfirm(true)}
        data-testid="delete-icon"
      />
      <Confirm
        open={confirm}
        title="Delete Feature?"
        description="Are you sure you want to permanently delete this feature? This action cannot be undone."
        confirmText="Yes, Delete"
        cancelText="No, Cancel"
        onConfirm={onDelete}
        onCancel={() => setConfirm(false)}
      />
    </>
  );
};

export default PermissionFeatureList;
